import type { CreateIntakeBody, OpsOrderIntakeSalePath } from "@/lib/orderIntakeClient";
import { applyPlantPacketUrlsToPayload } from "@/lib/ops/plantPacket";

export type InAppCreateKind = "inventory" | "customer";

export function parseInAppSaleAmountUsd(raw: string): number | null {
  const n = Number(raw.replace(/[$,]/g, "").trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

export function buildInAppCreateIntakeBody(input: {
  kind: InAppCreateKind;
  plantId: string;
  sellerLocationId: string;
  modelId: string;
  salePath: OpsOrderIntakeSalePath | "";
  endCustomerContactId: string;
  inventoryHolderOrgId: string;
  notes: string;
  saleAmountUsd: string;
  orderFormUrl: string;
  floorPlanUrl: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
}): CreateIntakeBody | { error: string } {
  if (!input.plantId) return { error: "Plant is required." };
  if (input.kind === "customer") {
    if (!input.salePath) return { error: "Sale path is required for customer orders." };
    if (!input.endCustomerContactId.trim()) {
      return { error: "End customer is required for customer orders." };
    }
  }

  const rawPayload: Record<string, unknown> = {
    submission_channel: "in_app_form",
  };
  const trimmedNotes = input.notes.trim();
  if (trimmedNotes) rawPayload.notes = trimmedNotes;
  const urls = applyPlantPacketUrlsToPayload(rawPayload, {
    orderFormUrl: input.orderFormUrl,
    floorPlanUrl: input.floorPlanUrl,
  });
  if (!urls.ok) return { error: urls.error };
  const customerName = input.customerName.trim();
  if (input.kind === "customer") {
    const total = parseInAppSaleAmountUsd(input.saleAmountUsd);
    if (total != null) rawPayload.total = total;
    const email = input.customerEmail.trim();
    const phone = input.customerPhone.trim();
    if (customerName || email || phone) {
      rawPayload.customer_contact = {
        id: input.endCustomerContactId.trim(),
        name: customerName,
        email,
        phone,
      };
    }
  }

  return {
    source: input.kind === "inventory" ? "in_app_inventory" : "in_app_customer",
    kind: input.kind,
    proposed_plant_id: input.plantId,
    proposed_seller_location_id: input.sellerLocationId.trim() || null,
    proposed_model_id: input.modelId || null,
    proposed_options: customerName ? { customer_name: customerName } : {},
    proposed_sale_path: input.kind === "customer" ? (input.salePath as OpsOrderIntakeSalePath) : null,
    proposed_inventory_holder_org_id:
      input.kind === "customer" ? input.inventoryHolderOrgId.trim() || null : null,
    proposed_end_customer_contact_id: input.kind === "customer" ? input.endCustomerContactId.trim() : null,
    raw_payload: rawPayload,
  };
}
